import React, { useEffect, useMemo, useState } from "react";
import Layout from "../layouts/Layout";
import {
  API_BASE_URL,
  FONT_HEADING,
  FONT_UI,
  SKIN_THEMES,
} from "../layouts/constants";

function useSkin() {
  const skinId = localStorage.getItem("skinTheme") || "navy";
  return SKIN_THEMES.find((s) => s.id === skinId) || SKIN_THEMES[0];
}

function api(path) {
  const base = API_BASE_URL.replace(/\/$/, "");
  if (base.endsWith("/api") && path.startsWith("/api/")) {
    return `${base}${path.replace(/^\/api/, "")}`;
  }
  return `${base}${path}`;
}

function formatISTDateTime(value) {
  if (!value) return "—";

  return new Date(value).toLocaleString("en-IN", {
    timeZone: "Asia/Kolkata",
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
}

function getAllocationKey(guard) {
  return String(
    guard.allocation_id ||
      guard.route_allocation_id ||
      guard.allocated_id ||
      `${guard.guard_id}-${guard.route_id}-${guard.allocated_start_time}-${guard.allocated_end_time}`
  );
}

const MIN_RANDOM_ROUTE_MINUTES = 120;

function getRouteDurationMinutes(startTime, endTime) {
  const start = new Date(startTime);
  const end = new Date(endTime);

  if (
    Number.isNaN(start.getTime()) ||
    Number.isNaN(end.getTime()) ||
    start >= end
  ) {
    return 0;
  }

  return Math.floor((end.getTime() - start.getTime()) / (1000 * 60));
}

export default function InCampusPage() {
  const skin = useSkin();

  const [guards, setGuards] = useState([]);
  const [checkType, setCheckType] = useState("random");
  const [selectedAllocationIds, setSelectedAllocationIds] = useState([]);
  const [scheduleTimes, setScheduleTimes] = useState({});
  const [generatedChecks, setGeneratedChecks] = useState([]);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const [dashboardData, setDashboardData] = useState({
    company_name: "",
    licensedFor: "",
    admin_active: 0,
    company_address: "",
    company_phone: "",
    admin_email: "",
    company_logo: "",
    user_map: "",
  });

  useEffect(() => {
    fetchPageData();
  }, []);

  useEffect(() => {
    if (!successMessage && !errorMessage) return;

    const timer = setTimeout(() => {
      setSuccessMessage("");
      setErrorMessage("");
    }, 5000);

    return () => clearTimeout(timer);
  }, [successMessage, errorMessage]);

  const routePage = useMemo(
    () =>
      dashboardData.user_map === "google"
        ? "/route-master"
        : "/route-master1",
    [dashboardData.user_map]
  );

  const fetchPageData = async () => {
    try {
      setLoading(true);
      setErrorMessage("");

      const token =
        localStorage.getItem("token") || localStorage.getItem("authToken");

      const [dashboardRes, guardsRes] = await Promise.all([
        fetch(api("/api/dashboard/admin/dashboard"), {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        }),
        fetch(api("/api/incampus/today-route-allocated-guards"), {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        }),
      ]);

      if (dashboardRes.ok) {
        const dashboardJson = await dashboardRes.json();
        const companyDetails = dashboardJson?.data?.companyDetails || {};
        setDashboardData((prev) => ({ ...prev, ...companyDetails }));
      }

      if (!guardsRes.ok) {
        throw new Error("Failed to load today allocated guard list");
      }

      const guardsJson = await guardsRes.json();
      setGuards(guardsJson?.data || guardsJson || []);
    } catch (error) {
      console.error(error);
      setErrorMessage(error.message || "Failed to load In Campus Check page.");
    } finally {
      setLoading(false);
    }
  };

  const toggleGuard = (allocationKey) => {
    setSelectedAllocationIds((prev) =>
      prev.includes(allocationKey)
        ? prev.filter((id) => id !== allocationKey)
        : [...prev, allocationKey]
    );

    setGeneratedChecks([]);
  };

  const generateRandomTimes = (startTime, endTime) => {
    const start = new Date(startTime);
    const end = new Date(endTime);

    if (
      Number.isNaN(start.getTime()) ||
      Number.isNaN(end.getTime()) ||
      start >= end
    ) {
      return [];
    }

    const durationMinutes = getRouteDurationMinutes(startTime, endTime);

    if (durationMinutes < MIN_RANDOM_ROUTE_MINUTES) {
      return [];
    }

    const times = [];
    const current = new Date(start);

    while (current < end) {
      const randomMinutes = Math.floor(Math.random() * 60);
      const checkTime = new Date(current);
      checkTime.setMinutes(checkTime.getMinutes() + randomMinutes);

      if (checkTime < end) {
        times.push(checkTime.toISOString());
      }

      current.setHours(current.getHours() + 2);
    }

    return times;
  };

  const handleGenerateChecks = () => {
    if (!selectedAllocationIds.length) {
      setErrorMessage("Please select at least one allocation.");
      return;
    }

    setErrorMessage("");
    setSuccessMessage("");

    const checks = [];

    for (const allocationKey of selectedAllocationIds) {
      const guard = guards.find((g) => getAllocationKey(g) === allocationKey);
      if (!guard) continue;

      if (checkType === "random") {
        const durationMinutes = getRouteDurationMinutes(
          guard.allocated_start_time,
          guard.allocated_end_time
        );

        if (durationMinutes < MIN_RANDOM_ROUTE_MINUTES) {
          setGeneratedChecks([]);
          setErrorMessage("This route is too small. Please try Schedule.");
          return;
        }

        const randomTimes = generateRandomTimes(
          guard.allocated_start_time,
          guard.allocated_end_time
        );

        randomTimes.forEach((time) => {
          checks.push({
            allocation_id:
              guard.allocation_id ||
              guard.route_allocation_id ||
              guard.allocated_id,
            guard_id: guard.guard_id,
            guard_name: guard.guard_name,
            route_id: guard.route_id,
            route_name: guard.route_name,
            check_type: "random",
            check_time: time,
          });
        });
      }

      if (checkType === "schedule") {
        const selectedTime = scheduleTimes[allocationKey];

        if (!selectedTime) {
          setErrorMessage(`Please select schedule time for ${guard.guard_name}`);
          return;
        }

        checks.push({
          allocation_id:
            guard.allocation_id ||
            guard.route_allocation_id ||
            guard.allocated_id,
          guard_id: guard.guard_id,
          guard_name: guard.guard_name,
          route_id: guard.route_id,
          route_name: guard.route_name,
          check_type: "schedule",
          check_time: selectedTime,
        });
      }
    }

    if (!checks.length) {
      setErrorMessage("No valid check time generated.");
      return;
    }

    setGeneratedChecks(checks);
    setSuccessMessage(`${checks.length} check time(s) generated.`);
  };

  const handleSubmit = async () => {
    if (!generatedChecks.length) {
      setErrorMessage("Please generate check times first.");
      return;
    }

    try {
      setSaving(true);
      setErrorMessage("");
      setSuccessMessage("");

      const token =
        localStorage.getItem("token") || localStorage.getItem("authToken");

      const res = await fetch(api("/api/incampus/in-campus-checks"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ checks: generatedChecks }),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok || data.success === false) {
        throw new Error(data.message || "Failed to save checks");
      }

      setSuccessMessage("In Campus checks saved successfully.");
      setSelectedAllocationIds([]);
      setScheduleTimes({});
      setGeneratedChecks([]);
    } catch (error) {
      console.error(error);
      setErrorMessage(error.message || "Failed to save In Campus checks.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={loadingWrapStyle}>
        <div style={spinnerStyle} />
        <p style={{ color: "#64748b", fontFamily: FONT_UI }}>
          Loading in-campus checks...
        </p>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <Layout dashboardData={dashboardData} routePage={routePage}>
      <PageHeader
        skin={skin}
        guardsCount={guards.length}
        selectedCount={selectedAllocationIds.length}
        generatedCount={generatedChecks.length}
        checkType={checkType}
      />

      {successMessage ? <Alert type="success" message={successMessage} /> : null}
      {errorMessage ? <Alert type="error" message={errorMessage} /> : null}

      <FormSection
        title="Allocation List"
        subtitle="Select guards and generate in-campus checking times."
        action={
          <div style={toggleWrapStyle}>
            <button
              type="button"
              onClick={() => {
                setCheckType("random");
                setGeneratedChecks([]);
                setErrorMessage("");
                setSuccessMessage("");
              }}
              style={{
                ...toggleBtnStyle,
                ...(checkType === "random"
                  ? { background: skin.gradient, color: "#fff" }
                  : {}),
              }}
            >
              Random
            </button>

            <button
              type="button"
              onClick={() => {
                setCheckType("schedule");
                setGeneratedChecks([]);
                setErrorMessage("");
                setSuccessMessage("");
              }}
              style={{
                ...toggleBtnStyle,
                ...(checkType === "schedule"
                  ? { background: skin.gradient, color: "#fff" }
                  : {}),
              }}
            >
              Schedule
            </button>
          </div>
        }
      >
        <TableShell>
          <table style={tableStyle}>
            <thead style={theadStyle}>
              <tr>
                <Th>Select</Th>
                <Th>Guard</Th>
                <Th>Route</Th>
                <Th>Start Time</Th>
                <Th>End Time</Th>
                {checkType === "schedule" ? <Th>Check Time</Th> : null}
              </tr>
            </thead>

            <tbody>
              {guards.length === 0 ? (
                <tr>
                  <td
                    colSpan={checkType === "schedule" ? 6 : 5}
                    style={emptyCellStyle}
                  >
                    No allocated guards found today.
                  </td>
                </tr>
              ) : (
                guards.map((guard) => {
                  const allocationKey = getAllocationKey(guard);

                  return (
                    <tr key={allocationKey} style={rowStyle}>
                      <Td>
                        <input
                          type="checkbox"
                          checked={selectedAllocationIds.includes(allocationKey)}
                          onChange={() => toggleGuard(allocationKey)}
                          style={checkboxStyle}
                        />
                      </Td>

                      <Td>
                        <div style={strongTextStyle}>{guard.guard_name}</div>
                        <div style={mutedTextStyle}>ID: {guard.guard_id}</div>
                      </Td>

                      <Td>
                        <div style={mediumTextStyle}>{guard.route_name}</div>
                        <div style={mutedTextStyle}>Route ID: {guard.route_id}</div>
                      </Td>

                      <Td>{formatISTDateTime(guard.allocated_start_time)}</Td>
                      <Td>{formatISTDateTime(guard.allocated_end_time)}</Td>

                      {checkType === "schedule" ? (
                        <Td>
                          <input
                            type="datetime-local"
                            value={scheduleTimes[allocationKey] || ""}
                            onChange={(e) =>
                              setScheduleTimes((prev) => ({
                                ...prev,
                                [allocationKey]: e.target.value,
                              }))
                            }
                            style={inputStyle}
                          />
                        </Td>
                      ) : null}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </TableShell>

        <div style={footerActionStyle}>
          <button
            type="button"
            onClick={handleGenerateChecks}
            disabled={!selectedAllocationIds.length}
            style={{
              ...primaryBtnStyle,
              background: skin.gradient,
              opacity: !selectedAllocationIds.length ? 0.55 : 1,
              cursor: !selectedAllocationIds.length ? "not-allowed" : "pointer",
            }}
          >
            Generate Check Time
          </button>
        </div>
      </FormSection>

      {generatedChecks.length > 0 ? (
        <FormSection
          title="Generated Checks"
          subtitle="Save these generated in-campus check records to database."
          action={
            <button
              type="button"
              onClick={handleSubmit}
              disabled={saving}
              style={{
                ...successBtnStyle,
                opacity: saving ? 0.7 : 1,
                cursor: saving ? "not-allowed" : "pointer",
              }}
            >
              {saving ? "Saving..." : "Save Checks"}
            </button>
          }
        >
          <TableShell>
            <table style={tableStyle}>
              <thead style={theadStyle}>
                <tr>
                  <Th>Guard</Th>
                  <Th>Route</Th>
                  <Th>Type</Th>
                  <Th>Check Time</Th>
                </tr>
              </thead>

              <tbody>
                {generatedChecks.map((check, index) => (
                  <tr
                    key={`${check.allocation_id || check.guard_id}-${check.check_time}-${index}`}
                    style={rowStyle}
                  >
                    <Td>
                      <div style={strongTextStyle}>{check.guard_name}</div>
                      <div style={mutedTextStyle}>ID: {check.guard_id}</div>
                    </Td>

                    <Td>
                      <div style={mediumTextStyle}>{check.route_name}</div>
                      <div style={mutedTextStyle}>Route ID: {check.route_id}</div>
                    </Td>

                    <Td>
                      <span
                        style={{
                          ...pillStyle,
                          background:
                            check.check_type === "random" ? "#dbeafe" : "#f3e8ff",
                          color:
                            check.check_type === "random" ? "#1d4ed8" : "#7e22ce",
                        }}
                      >
                        {check.check_type}
                      </span>
                    </Td>

                    <Td>{formatISTDateTime(check.check_time)}</Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableShell>
        </FormSection>
      ) : null}
    </Layout>
  );
}

function PageHeader({
  skin,
  guardsCount,
  selectedCount,
  generatedCount,
  checkType,
}) {
  return (
    <>
      <div style={{ marginBottom: 24 }}>
        <h2 style={pageTitleStyle}>In Campus Check</h2>
        <p style={pageSubStyle}>Today route allocated guard list</p>
      </div>

      <div style={topCardStyle}>
        <div>
          <h3 style={cardTitleStyle}>In Campus Checking</h3>
          <p style={cardSubStyle}>
            Generate random or scheduled guard check times for campus routes
          </p>
        </div>

        <div style={statsWrapStyle}>
          <Stat label="Guards" value={guardsCount} />
          <Stat label="Selected" value={selectedCount} />
          <Stat label="Generated" value={generatedCount} />
          <span style={{ ...pillStyle, background: skin.gradient, color: "#fff" }}>
            {checkType === "random" ? "Random Check" : "Schedule Check"}
          </span>
        </div>
      </div>
    </>
  );
}

function Stat({ label, value }) {
  return (
    <div style={statStyle}>
      <span style={statLabelStyle}>{label}</span>
      <strong style={statValueStyle}>{value}</strong>
    </div>
  );
}

function FormSection({ title, subtitle, action, children }) {
  return (
    <div style={formSectionStyle}>
      <div style={sectionHeaderStyle}>
        <div>
          <h3 style={cardTitleStyle}>{title}</h3>
          <p style={cardSubStyle}>{subtitle}</p>
        </div>
        {action}
      </div>
      {children}
    </div>
  );
}

function Alert({ type, message }) {
  const isSuccess = type === "success";

  return (
    <div
      style={{
        marginBottom: 18,
        padding: "14px 16px",
        borderRadius: 12,
        fontSize: 14,
        fontWeight: 500,
        fontFamily: FONT_UI,
        border: `1px solid ${isSuccess ? "#bbf7d0" : "#fecaca"}`,
        background: isSuccess ? "#f0fdf4" : "#fef2f2",
        color: isSuccess ? "#166534" : "#b91c1c",
      }}
    >
      {message}
    </div>
  );
}

function TableShell({ children }) {
  return <div style={tableShellStyle}>{children}</div>;
}

function Th({ children }) {
  return <th style={thStyle}>{children}</th>;
}

function Td({ children }) {
  return <td style={tdStyle}>{children}</td>;
}

const loadingWrapStyle = {
  minHeight: "100vh",
  background: "#f1f5f9",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
};

const spinnerStyle = {
  width: 40,
  height: 40,
  borderRadius: "50%",
  border: "4px solid #dbeafe",
  borderTopColor: "#2563eb",
  animation: "spin 0.8s linear infinite",
  marginBottom: 12,
};

const pageTitleStyle = {
  margin: 0,
  fontSize: 26,
  fontWeight: 400,
  color: "#0f172a",
  fontFamily: FONT_HEADING,
  letterSpacing: -0.3,
};

const pageSubStyle = {
  margin: "4px 0 0",
  fontSize: 14,
  color: "#64748b",
  fontFamily: FONT_UI,
};

const topCardStyle = {
  background: "#fff",
  borderRadius: 18,
  padding: "24px 28px",
  boxShadow: "0 1px 6px rgba(0,0,0,0.06)",
  border: "1px solid #f1f5f9",
  marginBottom: 22,
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: 16,
  flexWrap: "wrap",
};

const formSectionStyle = {
  background: "#fff",
  borderRadius: 18,
  padding: "24px 28px",
  boxShadow: "0 1px 6px rgba(0,0,0,0.06)",
  border: "1px solid #f1f5f9",
  marginBottom: 22,
};

const sectionHeaderStyle = {
  marginBottom: 18,
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: 16,
  flexWrap: "wrap",
};

const cardTitleStyle = {
  margin: "0 0 4px",
  fontSize: 22,
  fontWeight: 400,
  color: "#0f172a",
  fontFamily: FONT_HEADING,
};

const cardSubStyle = {
  margin: 0,
  color: "#64748b",
  fontSize: 14,
  fontFamily: FONT_UI,
};

const statsWrapStyle = {
  display: "flex",
  gap: 10,
  flexWrap: "wrap",
  alignItems: "center",
};

const statStyle = {
  border: "1px solid #e2e8f0",
  background: "#f8fafc",
  borderRadius: 12,
  padding: "8px 12px",
  minWidth: 82,
};

const statLabelStyle = {
  display: "block",
  fontSize: 11,
  color: "#64748b",
  fontFamily: FONT_UI,
};

const statValueStyle = {
  display: "block",
  marginTop: 2,
  fontSize: 16,
  color: "#0f172a",
  fontFamily: FONT_UI,
};

const toggleWrapStyle = {
  display: "flex",
  gap: 6,
  background: "#f1f5f9",
  padding: 5,
  borderRadius: 999,
};

const toggleBtnStyle = {
  border: "none",
  outline: "none",
  borderRadius: 999,
  padding: "9px 18px",
  fontSize: 13,
  fontWeight: 700,
  color: "#475569",
  background: "transparent",
  cursor: "pointer",
  fontFamily: FONT_UI,
};

const tableShellStyle = {
  width: "100%",
  overflowX: "auto",
  border: "1px solid #e2e8f0",
  borderRadius: 14,
};

const tableStyle = {
  width: "100%",
  borderCollapse: "collapse",
  textAlign: "left",
  fontSize: 14,
  fontFamily: FONT_UI,
};

const theadStyle = {
  background: "#f8fafc",
};

const thStyle = {
  padding: "14px 16px",
  color: "#475569",
  fontSize: 12,
  fontWeight: 800,
  textTransform: "uppercase",
  borderBottom: "1px solid #e2e8f0",
  whiteSpace: "nowrap",
};

const tdStyle = {
  padding: "14px 16px",
  color: "#334155",
  borderBottom: "1px solid #f1f5f9",
  verticalAlign: "middle",
  whiteSpace: "nowrap",
};

const rowStyle = {
  background: "#fff",
};

const emptyCellStyle = {
  padding: "42px 16px",
  textAlign: "center",
  color: "#64748b",
  fontFamily: FONT_UI,
};

const strongTextStyle = {
  fontWeight: 700,
  color: "#0f172a",
};

const mediumTextStyle = {
  fontWeight: 600,
  color: "#334155",
};

const mutedTextStyle = {
  marginTop: 3,
  fontSize: 12,
  color: "#64748b",
};

const inputStyle = {
  width: "100%",
  padding: "12px 14px",
  borderRadius: 10,
  border: "1px solid #dbe2ea",
  outline: "none",
  fontSize: 14,
  color: "#0f172a",
  background: "#fff",
  fontFamily: FONT_UI,
  boxSizing: "border-box",
};

const checkboxStyle = {
  width: 16,
  height: 16,
  cursor: "pointer",
};

const footerActionStyle = {
  marginTop: 18,
  display: "flex",
  justifyContent: "flex-end",
};

const primaryBtnStyle = {
  border: "none",
  outline: "none",
  padding: "11px 18px",
  borderRadius: 10,
  fontSize: 14,
  fontWeight: 600,
  color: "#fff",
  background: "linear-gradient(135deg, #1e293b 0%, #2563eb 100%)",
  fontFamily: FONT_UI,
  boxShadow: "0 8px 18px rgba(37, 99, 235, 0.18)",
  cursor: "pointer",
};

const successBtnStyle = {
  ...primaryBtnStyle,
  background: "#16a34a",
  boxShadow: "0 8px 18px rgba(22, 163, 74, 0.18)",
};

const pillStyle = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: 999,
  padding: "7px 12px",
  fontSize: 12,
  fontWeight: 800,
  fontFamily: FONT_UI,
  whiteSpace: "nowrap",
};